import { useState, useEffect, useCallback } from 'react';
import { addDoc, updateDoc, deleteDoc, doc, getDoc, collection, getFirestore } from 'firebase/firestore';
import { app } from '../services/firebase';
import type { Field as OriginalField, FieldValidator } from '../components/common/Form/Form';
import type { ZodSchema, ZodError } from "zod"; // <-- Importa tipos do Zod

export interface Field extends OriginalField {
  validators?: FieldValidator[];
}

interface UseFormHandlerProps<T = Record<string, unknown>, R = unknown> {
  collectionName: string;
  documentId?: string;
  mode: 'create' | 'read' | 'update';
  api?: {
    create?: (data: T) => Promise<R>;
    update?: (id: string, data: T) => Promise<R>;
    patch?: (id: string, data: Partial<T>) => Promise<R>;
    delete?: (id: string) => Promise<R>;
    get?: (id: string) => Promise<T>;
  };
  schema?: ZodSchema<T>; // <-- Adicione o schema Zod como opcional
}

const useFormHandler = <T = Record<string, unknown>, R = unknown>({
  collectionName,
  documentId,
  mode,
  api,
  schema, // <-- Recebe o schema
}: UseFormHandlerProps<T, R>) => {
  // NOVO: detecção de ambiente de teste / ausência de Firestore
  const isTestEnv = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';

  // Tentar inicializar firestore com segurança
  let firestore: ReturnType<typeof getFirestore> | undefined;
  if (!isTestEnv && app) {
    try {
      firestore = getFirestore(app);
    } catch {
      // Silencia erro se app não inicializado
      firestore = undefined;
    }
  }

  const hasFirestore = !!firestore;

  const [formData, setFormData] = useState<{ [key: string]: string | boolean | string[] | File[] }>({});
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    visible: boolean;
    title?: string;
    description?: string;
  }>({
    message: '',
    type: 'success',
    visible: false,
  });
  const [fieldStatus, setFieldStatus] = useState<Record<string, { error?: string; success?: string }>>({});
  const [zodError, setZodError] = useState<ZodError<T> | null>(null);

  // Carrega os dados do documento (protege contra ausência de Firestore)
  useEffect(() => {
    if ((mode === 'read' || mode === 'update') && documentId) {
      const fetchDocument = async () => {
        try {
          if (api?.get) {
            const data = await api.get(documentId);
            setFormData(data as { [key: string]: string | boolean | string[] | File[] });
          } else if (hasFirestore && firestore) {
            const docRef = doc(firestore, collectionName, documentId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              setFormData(docSnap.data() as { [key: string]: string | boolean | string[] | File[] });
            } else {
              setToast({ message: 'Documento não encontrado.', type: 'error', visible: true });
            }
          } else {
            // Fallback silencioso em testes sem API / Firestore
            if (!isTestEnv) {
              setToast({ message: 'Firestore indisponível.', type: 'error', visible: true });
            }
          }
        } catch (error) {
          if (error instanceof Error) {
            setToast({ message: error.message || 'Erro inesperado.', type: 'error', visible: true });
          } else {
            setToast({ message: 'Erro inesperado.', type: 'error', visible: true });
          }
        }
      };
      fetchDocument();
    }
  }, [mode, documentId, collectionName, firestore, api, hasFirestore, isTestEnv]);

  const handleFieldChange = useCallback((id: string, value: string | boolean | string[] | File[]) => {
    setFormData((prev) => ({ ...prev, [id]: value }));
  }, []);

  const runFieldValidators = useCallback(
    async (field: Field, value: string | boolean | File[] | undefined) => {
      if (!field.validators) return { error: undefined, success: undefined };
      for (const validator of field.validators) {
        if (validator.type === 'sync') {
          if (!validator.validate(value, formData)) {
            return { error: validator.message, success: undefined };
          }
        } else if (validator.type === 'async') {
          const valid = await validator.validate(value, formData);
          if (!valid) {
            return { error: validator.message, success: undefined };
          }
        } else if (validator.type === 'remote' && validator.endpoint) {
          try {
            const payload = validator.mapRequest
              ? validator.mapRequest(value, formData)
              : { value, formData };
            const response = await fetch(validator.endpoint, {
              method: validator.method || 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: validator.method === 'GET' ? undefined : JSON.stringify(payload),
            });
            const data = await response.json();
            const result = validator.mapResponse
              ? validator.mapResponse(data)
              : { valid: !!data.valid, message: data.message };
            if (!result.valid) {
              return { error: result.message || validator.message, success: undefined };
            }
          } catch (err) {
            return { error: validator.message || 'Erro de validação remota.', success: undefined };
          }
        }
      }
      return { error: undefined, success: 'Campo válido!' };
    },
    [formData]
  );

  const handleFieldChangeWithValidation = useCallback(
    async (
      id: string,
      value: string | boolean | File[] | string[] | undefined,
      fieldsArray: Field[][],
      onChange?: (id: string, value: unknown, formData: Record<string, unknown>) => void,
      onError?: (fieldId: string, error: string) => void,
      onValid?: (fieldId: string) => void
    ) => {
      handleFieldChange(id, value === undefined ? '' : value);
      if (onChange) onChange(id, value, { ...formData, [id]: value });
      // Find the field by id
      const field = fieldsArray.flat().find(f => f.id === id);
      if (!field) return;
      const status = await runFieldValidators(field, value as string | boolean | File[] | undefined);
      setFieldStatus((prev) => ({ ...prev, [id]: status }));
      if (status.error && onError) onError(id, status.error);
      if (!status.error && onValid) onValid(id);
    },
    [handleFieldChange, formData, runFieldValidators]
  );

  const handleBlurWithValidation = useCallback(
    async (
      field: Field,
      value: string | boolean | File[] | undefined
    ) => {
      const status = await runFieldValidators(field, value);
      setFieldStatus((prev) => ({ ...prev, [field.id]: status }));
    },
    [runFieldValidators]
  );

  // Função para validar com Zod
  const validateWithZod = useCallback((data: T) => {
    if (!schema) return true;
    const result = schema.safeParse(data);
    if (!result.success) {
      setZodError(result.error);
      setToast({ message: "Dados inválidos.", type: 'error', visible: true });
      return false;
    }
    setZodError(null);
    return true;
  }, [schema]);

  // Substitua as funções padrão se api for fornecida
  const handleCreate = useCallback(async () => {
    try {
      if (schema && !validateWithZod(formData as T)) return;
      if (api?.create) {
        await api.create(formData as T);
      } else if (hasFirestore && firestore) {
        const docRef = await addDoc(collection(firestore, collectionName), formData);
        console.log('Documento criado com ID:', docRef.id);
      } else {
        // Fallback em teste: simula operação
        if (!isTestEnv) console.warn('Create ignorado: Firestore indisponível.');
      }
      setToast({ message: 'Documento criado com sucesso!', type: 'success', visible: true });
    } catch (error) {
      if (error instanceof Error) {
        setToast({ message: error.message || 'Erro inesperado.', type: 'error', visible: true });
      } else {
        setToast({ message: 'Erro inesperado.', type: 'error', visible: true });
      }
    }
  }, [formData, collectionName, firestore, api, schema, validateWithZod, hasFirestore, isTestEnv]);

  const handleUpdate = useCallback(async () => {
    if (!documentId) return;
    try {
      if (schema && !validateWithZod(formData as T)) return;
      if (api?.update) {
        await api.update(documentId, formData as T);
      } else if (hasFirestore && firestore) {
        const docRef = doc(firestore, collectionName, documentId);
        await updateDoc(docRef, formData);
      } else {
        if (!isTestEnv) console.warn('Update ignorado: Firestore indisponível.');
      }
      setToast({ message: 'Documento atualizado com sucesso!', type: 'success', visible: true });
    } catch (error) {
      if (error instanceof Error) {
        setToast({ message: error.message || 'Erro inesperado.', type: 'error', visible: true });
      } else {
        setToast({ message: 'Erro inesperado.', type: 'error', visible: true });
      }
    }
  }, [formData, collectionName, documentId, firestore, api, schema, validateWithZod, hasFirestore, isTestEnv]);

  const handleDelete = useCallback(async () => {
    if (!documentId) return;
    try {
      if (api?.delete) {
        await api.delete(documentId);
      } else if (hasFirestore && firestore) {
        const docRef = doc(firestore, collectionName, documentId);
        await deleteDoc(docRef);
      } else {
        if (!isTestEnv) console.warn('Delete ignorado: Firestore indisponível.');
      }
      setToast({ message: 'Documento deletado com sucesso!', type: 'success', visible: true });
    } catch (error) {
      if (error instanceof Error) {
        setToast({ message: error.message || 'Erro inesperado.', type: 'error', visible: true });
      } else {
        setToast({ message: 'Erro inesperado.', type: 'error', visible: true });
      }
    }
  }, [collectionName, documentId, firestore, api, hasFirestore, isTestEnv]);

  const handleLogin = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        const data = await response.json();
        if (!isTestEnv) {
          document.cookie = `authToken=${data.token}; path=/; secure; samesite=strict`;
          window.location.href = data.redirectUrl || '/';
        }
        setToast({ message: 'Login realizado com sucesso!', type: 'success', visible: true });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Falha no login');
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setToast({ message: error.message || 'Erro inesperado.', type: 'error', visible: true });
      } else {
        setToast({ message: 'Erro inesperado.', type: 'error', visible: true });
      }
    }
  }, [formData, isTestEnv]);

  const handlePatch = useCallback(async (partialData: Partial<T>) => {
    if (!documentId) return;
    try {
      if (api?.patch) {
        await api.patch(documentId, partialData);
      } else if (hasFirestore && firestore) {
        const docRef = doc(firestore, collectionName, documentId);
        await updateDoc(docRef, partialData);
      } else {
        if (!isTestEnv) console.warn('Patch ignorado: Firestore indisponível.');
      }
      setToast({ message: 'Alterações salvas!', type: 'success', visible: true });
    } catch (error) {
      if (error instanceof Error) {
        setToast({ message: error.message || 'Erro inesperado.', type: 'error', visible: true });
      } else {
        setToast({ message: 'Erro inesperado.', type: 'error', visible: true });
      }
    }
  }, [collectionName, documentId, firestore, api, hasFirestore, isTestEnv]);

  // Salvar rascunho localmente
  const saveDraft = useCallback(async () => {
    localStorage.setItem(`draft_${collectionName}_${documentId || 'new'}`, JSON.stringify(formData));
    setToast({ message: 'Rascunho salvo localmente!', type: 'success', visible: true });
  }, [formData, collectionName, documentId]);

  // Carregar rascunho ao iniciar
  useEffect(() => {
    const draft = localStorage.getItem(`draft_${collectionName}_${documentId || 'new'}`);
    if (draft) setFormData(JSON.parse(draft));
  }, [collectionName, documentId]);

  // Função para lidar com foco
  const handleFocus = useCallback((fieldId: string) => {
    console.log(`Campo ${fieldId} recebeu foco`);
    // Adicione lógica adicional aqui, como destacar o campo
  }, []);

  // Função para lidar com perda de foco
  const handleBlur = useCallback((fieldId: string) => {
    console.log(`Campo ${fieldId} perdeu foco`);
    // Adicione lógica adicional aqui, como validação
  }, []);

  // Resetar para valores iniciais (ou vazios)
  const resetForm = useCallback((initialValues?: Partial<T>) => {
    if (initialValues) {
      // Remove undefined values to satisfy the state type
      const filtered = Object.entries(initialValues).reduce<{ [key: string]: string | boolean | string[] | File[] }>((acc, [key, value]) => {
        if (value !== undefined) acc[key] = value as string | boolean | string[] | File[];
        return acc;
      }, {});
      setFormData(filtered);
    } else {
      setFormData({});
    }
  }, []);

  // Limpar todos os campos
  const clearForm = useCallback(() => {
    setFormData({});
  }, []);

  // Setar valores arbitrários
  const setValues = useCallback((values: Partial<T>) => {
    setFormData((prev) => ({ ...prev, ...values }));
  }, []);

  return {
    formData,
    toast,
    setToast,
    handleFieldChange,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleLogin,
    handleFocus,
    handleBlur,
    handlePatch,
    saveDraft,
    resetForm,
    clearForm,
    setValues,
    fieldStatus,
    runFieldValidators,
    handleFieldChangeWithValidation,
    handleBlurWithValidation,
    zodError, // <-- Exporte o erro do Zod
    validateWithZod, // <-- Exporte a função de validação
  };
};

export default useFormHandler;